import { z } from 'zod/v4-mini';

import { GeneralBackendResponseSchema } from '$/services/mapper/authentication';

export const ProjectFolderSchema = z.object({
  id: z.string(),
  name: z.string(),
  userId: z.string(),
  schoolClassId: z.optional(z.string()),
});

export type ProjectFolder = z.infer<typeof ProjectFolderSchema>;

export const AIProjectInitializationAnalysisResultSchema = z.object({
  analysis_version: z.optional(z.string()),
  analysis_timestamp: z.optional(z.string()),
  language: z.optional(z.string()),

  scene_type: z.enum(['Interior', 'Exterior']),
  scene_type_localized: z.string(),
  visible_story_count: z.nullable(z.number()),
  has_non_vegetation_obstructions: z.boolean(),
  obstruction_objects: z.nullable(z.array(z.string())),
  obstruction_objects_localized: z.nullable(z.array(z.string())),
  is_perspective_acceptable: z.enum(['Yes', 'No']),
  correction_prompt: z.nullable(z.string()),
  correction_prompt_localized: z.nullable(z.string()),

  camera_azimuth: z.optional(z.string()),
  camera_elevation: z.optional(z.string()),
  camera_distance: z.optional(z.string()),

  has_facade_discolorations: z.optional(z.boolean()),
  facade_discolorations: z.optional(z.nullable(z.array(z.string()))),
  facade_discolorations_localized: z.optional(z.nullable(z.array(z.string()))),

  negative_prompt: z.optional(z.nullable(z.string())),
  interior_analysis: z.nullish(
    z.looseObject({
      baseline_colors: z.optional(
        z.array(
          z.looseObject({
            hex_code: z.string(),
            element: z.string(),
            color_name: z.string(),
          }),
        ),
      ),
    }),
  ),

  color_analysis: z.optional(z.unknown()),

  uncertainties: z.optional(
    z.object({
      overall_confidence: z.string(),
      notes: z.nullable(z.string()),
    }),
  ),
});

export const CreateEditorProjectResponseSchema = z.object({
  ...GeneralBackendResponseSchema,
  payload: z.object({
    id: z.string(),
    type: z.literal('editor'),
    userId: z.string(),
    projectName: z.string(),
    isPublic: z.boolean(),
    ai: z.optional(
      z.object({
        sceneAnalysis: z.optional(AIProjectInitializationAnalysisResultSchema),
      }),
    ),
  }),
});

export const CameraViewpoints = [
  'top_down',
  'birds_eye',
  'high_angle',
  'above_shot',
  'slightly_above',
  'straight_on',
  'hero',
  'low',
  'worms_eye',
];

export type CameraViewpoint = (typeof CameraViewpoints)[number];

export const EditorProjectItemSchema = z.object({
  id: z.string(),
  userId: z.string(),
  projectName: z.string(),
  isPublic: z.boolean(),
  type: z.literal('editor'),
  parentId: z.optional(z.string()),
  schoolClassId: z.optional(z.string()),
  schoolClassIds: z.optional(z.array(z.string())),
  projectThumbnail: z.optional(z.url()),
  data: z.optional(
    z.object({
      elements: z.array(z.any()),
      components: z.optional(z.array(z.any())),
    }),
  ),
  ai: z.optional(
    z.object({
      sceneAnalysis: z.optional(AIProjectInitializationAnalysisResultSchema),
    }),
  ),
});

export type EditorProject = z.infer<typeof EditorProjectItemSchema>;

export type LocalEditorProject = Pick<
  EditorProject,
  'projectName' | 'type' | 'data'
> & {
  image: File;
};

export const GetEditorProjectResponseSchema = z.object({
  ...GeneralBackendResponseSchema,
  payload: z.optional(EditorProjectItemSchema),
});

export const GetProjectFoldersResponseSchema = z.object({
  ...GeneralBackendResponseSchema,
  payload: z.array(ProjectFolderSchema),
});

export const RefineEditorProjectResponseSchema = z.object({
  ...GeneralBackendResponseSchema,
  payload: z.object({
    imageSrc: z.string(),
  }),
});

export const ReprojectEditorProjectResponseSchema = z.object({
  ...GeneralBackendResponseSchema,
  payload: z.object({
    imageSrc: z.string(),
  }),
});
